// Light decomposition (PR 14 ST 7 Q2 — extract row only; orchestrator
// stays in StaffDashboard.jsx). Presentational + controlled: parent
// owns mutations and passes typed callbacks.

import CoverThumb from "@staff/features/shared/CoverThumb";
import { Button } from "@shared/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/components/ui/alert-dialog";
import { Pencil, Trash2, QrCode } from "lucide-react";
import type { ExhibitListItem } from "@shared/api/exhibits";
import Highlight from "@staff/features/shared/Highlight";
import NarrationBadges from "@staff/features/narration/NarrationBadges";

interface ExhibitListRowProps {
  exhibit: ExhibitListItem;
  onTogglePublish: (id: string, state: ExhibitListItem["state"]) => void;
  onGenerateQR: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  /** F-U: search text to highlight in the code + titles. */
  query?: string;
  /** F-U batch selection; the column is omitted when `onSelect` is absent
   *  (analysts). Archived / purged rows render an empty cell. */
  selected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  /** F-V: open the exhibit on its Narration tab (badge click). */
  onOpenNarration?: (id: string) => void;
}

const ExhibitListRow = ({
  exhibit,
  onTogglePublish,
  onGenerateQR,
  onEdit,
  onDelete,
  query = "",
  selected = false,
  onSelect,
  onOpenNarration,
}: ExhibitListRowProps) => {
  const isPublished = exhibit.state === "published";
  const isArchived = exhibit.state === "archived";
  const isPurged = exhibit.state === "purged";
  const isInactive = isArchived || isPurged;

  return (
    <tr
      data-testid={`exhibit-row-${exhibit.id}`}
      className={selected ? "bg-primary/5" : undefined}
    >
      {onSelect && (
        <td className="pl-6 pr-2 py-4 w-8">
          {!isInactive && (
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => onSelect(exhibit.id, e.target.checked)}
              aria-label={`Select exhibit ${exhibit.code || exhibit.titleTc}`}
              data-testid={`select-exhibit-${exhibit.id}`}
            />
          )}
        </td>
      )}
      <td className="px-6 py-4">
        <div
          className="font-mono text-2xl font-bold text-primary"
          data-testid={`exhibit-code-${exhibit.id}`}
        >
          {exhibit.code ? (
            <Highlight text={exhibit.code} query={query} />
          ) : (
            "---"
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <CoverThumb
          coverImageId={exhibit.coverImageId}
          alt={exhibit.titleTc}
          testId={`exhibit-image-${exhibit.id}`}
        />
      </td>
      <td className="px-6 py-4">
        <div
          className="font-medium"
          data-testid={`exhibit-title-tc-${exhibit.id}`}
        >
          <Highlight text={exhibit.titleTc} query={query} />
        </div>
        <div className="text-sm text-muted-foreground">
          <Highlight text={exhibit.titleEn} query={query} />
        </div>
        {/* SC title surfaces only when the match is in it. */}
        {query.trim() !== "" &&
          (exhibit.titleSc ?? "")
            .toLowerCase()
            .includes(query.trim().toLowerCase()) && (
            <div className="text-sm text-muted-foreground">
              <Highlight text={exhibit.titleSc} query={query} />
            </div>
          )}
      </td>
      <td
        className="px-6 py-4 text-sm"
        data-testid={`exhibit-exhibition-${exhibit.id}`}
      >
        {exhibit.exhibitionTitleTc ?? (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td
        className="px-6 py-4 text-sm"
        data-testid={`exhibit-zone-${exhibit.id}`}
      >
        {exhibit.zoneTitleTc ?? (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-6 py-4">
        {isArchived ? (
          <span
            className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
            title="Restore the parent exhibition to bring this exhibit back"
            data-testid={`publish-toggle-${exhibit.id}`}
          >
            ▼ Archived
          </span>
        ) : isPurged ? (
          <span
            className="px-3 py-1 rounded-full text-xs font-medium bg-zinc-200 text-zinc-600"
            title="Exhibit has been purged — tombstone only, asset bytes removed"
            data-testid={`publish-toggle-${exhibit.id}`}
          >
            ✕ Purged
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onTogglePublish(exhibit.id, exhibit.state)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              isPublished
                ? "bg-green-100 text-green-700"
                : "bg-zinc-100 text-zinc-700"
            }`}
            data-testid={`publish-toggle-${exhibit.id}`}
          >
            {isPublished ? "Published" : "Unpublished"}
          </button>
        )}
      </td>
      <td className="px-6 py-4">
        {exhibit.hasQr ? (
          <span
            className="text-green-600 text-sm"
            data-testid={`qr-status-${exhibit.id}`}
          >
            Generated
          </span>
        ) : isInactive ? (
          <span className="text-muted-foreground text-sm">—</span>
        ) : (
          <Button
            onClick={() => onGenerateQR(exhibit.id)}
            variant="outline"
            size="sm"
            data-testid={`generate-qr-${exhibit.id}`}
          >
            <QrCode className="w-4 h-4 mr-1" />
            Generate
          </Button>
        )}
      </td>
      <td className="px-6 py-4">
        <NarrationBadges
          ownerId={exhibit.id}
          status={exhibit.narrationStatus}
          onOpen={onOpenNarration ?? onEdit}
        />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => onEdit(exhibit.id)}
            variant="ghost"
            size="sm"
            data-testid={`edit-button-${exhibit.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          {!isInactive && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  data-testid={`delete-button-${exhibit.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Exhibit</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(exhibit.id)}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ExhibitListRow;
